import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { useTranslation } from 'react-i18next';
import { fetchFloorViewSvg } from '@apis/storage';
import { professionColor } from '@utils/professionColor';
import { tradeLabel } from '@utils/tradeLabel';
import { areaHighlightSelector } from '@utils/svgMarkers';
import { useAppSelector } from '@store/hooks';
import {
  aggregateUtilitiesByType,
  selectEquipmentByFloorAndArea,
  selectEquipmentByFloorId,
  selectFloorChips,
  selectFloors,
  selectManagersByFloorAndArea,
  selectManagersByFloorId,
  selectMonitorLayer,
  selectProfessions,
  selectSafetyEvents,
  selectUtilitiesByFloorAndArea,
  selectUtilitiesByFloorId,
  type EquipmentPresence,
  type ManagerPresence,
  type UtilityPresence,
} from '@store/selectors/monitor.selectors';
import { useSafetyVisible, useSetSelectedFloor } from '../hooks';
import { useItemDrawer } from '../layers/useItemDrawer';
import { useFloorItems } from '../layers/useLayerData';
import { FloorPlanModal, FloorPlanView, type TradePresence } from './FloorPlanView';
import {
  ActivitiesPanel,
  IssuesPanel,
  PersonnelPanel,
  SafetyPanel,
} from './FloorDrawerPanels';
import {
  ActivitiesIcon,
  CloseIcon,
  ExpandIcon,
  IssuesIcon,
  PersonnelIcon,
  SafetyIcon,
} from './floorDrawerIcons';
import styles from './floorDrawer.module.css';

type IFloorDrawerProps = {
  floorId: string;
};

type DrawerTab = 'personnel' | 'activities' | 'issues' | 'safety';

// Anchor reading + viewBox tightening are shared with EquipmentFloorPlan — see
// `readSvgAnchorCentres` / `tightenSvgViewBox` in `@utils/svgMarkers`.

/**
 * Slide-in drawer that opens when a floor is selected on the building view.
 * Renders the per-floor SVG fetched from the proxied storage endpoint plus
 * a tab bar (Personnel / Activities / Issues / Safety) with floor-scoped
 * content — mirrors trusstor_dashboard's FloorView panel.
 *
 * Per-area worker chip overlay and the "active area" tint are wired in
 * `FloorPlanView` + the CSS-injection effect on this component
 * — area highlights paint via inline style on each `area<id>` element
 * with workers, mirroring trusstor_dashboard's `paintSelectedAreas`.
 */
export function FloorDrawer({ floorId }: IFloorDrawerProps) {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const setSelectedFloor = useSetSelectedFloor();
  // The drawer opens on the active layer's tab, and follows a layer switch.
  const layer = useAppSelector(selectMonitorLayer);
  const layerTab: DrawerTab = layer === 'workforce' ? 'personnel' : layer;
  const [tab, setTab] = useState<DrawerTab>(layerTab);
  const [tabLayer, setTabLayer] = useState(layer);
  if (tabLayer !== layer) {
    setTabLayer(layer);
    setTab(layerTab);
  }
  const [isModalOpen, setIsModalOpen] = useState(false);
  const safetyVisible = useSafetyVisible();

  const floorChips = useAppSelector(selectFloorChips);
  const floors = useAppSelector(selectFloors);
  const professions = useAppSelector(selectProfessions);
  const safetyEvents = useAppSelector(selectSafetyEvents);
  const equipmentByFloorArea = useAppSelector(selectEquipmentByFloorAndArea);
  const utilitiesByFloorArea = useAppSelector(selectUtilitiesByFloorAndArea);
  const managersByFloorArea = useAppSelector(selectManagersByFloorAndArea);
  // Floor-level (not per-area) presences for the Personnel list, which lists
  // the whole floor. Same selectors the building-view hover popover uses, so
  // the drawer's list and the popover stay in agreement.
  const managersByFloor = useAppSelector(selectManagersByFloorId);
  const equipmentByFloor = useAppSelector(selectEquipmentByFloorId);
  const utilitiesByFloor = useAppSelector(selectUtilitiesByFloorId);

  const floorChip = useMemo(
    () => floorChips.find((f) => f.floorId === floorId) ?? null,
    [floorChips, floorId],
  );
  const rawFloor = useMemo(
    () => floors.find((f) => f.floorId === floorId) ?? null,
    [floors, floorId],
  );
  const profById = useMemo(() => new Map(professions.map((p) => [p._id, p])), [professions]);

  const personnel = useMemo(() => {
    if (!rawFloor) return [];
    return rawFloor.activeProfessions
      .map((p) => {
        const full = profById.get(p._id);
        return {
          id: p._id,
          tradeId: full?.tradeId ?? p.tradeId,
          tradeGroup: full?.tradeGroup ?? p.tradeGroup,
          color: professionColor(full?.backgroundColor),
          tagIds: p.activeTagsIds,
        };
      })
      .filter((row) => row.tagIds.length > 0)
      .sort((a, b) => b.tagIds.length - a.tagIds.length);
  }, [rawFloor, profById]);

  // The same items, under the same pills, as the layer's list and chips.
  const floorItems = useFloorItems(floorId);
  // The drawer only mounts on the site-scoped Monitor route.
  const itemDrawer = useItemDrawer(siteId!);
  const activitiesOnFloor = floorItems.activities;
  const issuesOnFloor = floorItems.issues;
  const safetyOnFloor = useMemo(
    () => safetyEvents.filter((e) => e.floorId === floorId),
    [safetyEvents, floorId],
  );

  const personnelCount = personnel.reduce((sum, p) => sum + p.tagIds.length, 0);

  // Per-area trade breakdown. `IMergedFloorView.areas[].activeProfessions[]`
  // tells us which trades are present in each area and how many tags
  // each carries; we look up the profession's display label + color from
  // the catalog (`profById`). Map keyed by lowercased areaId so SVG ids
  // can be matched case-insensitively.
  const personnelByAreaId = useMemo(() => {
    const m = new Map<string, TradePresence[]>();
    if (!rawFloor) return m;
    for (const area of rawFloor.areas ?? []) {
      // Aggregate by tradeId (specific trade), not tradeGroup — multiple
      // professions sharing the broad category should still render as
      // separate chips (e.g. "fireProtection" + "electrical" are both in
      // the "systems" group but display distinctly). Mirrors trusstor.
      const byTrade = new Map<string, TradePresence>();
      for (const p of area.activeProfessions) {
        if (p.activeTagsIds.length === 0) continue;
        const full = profById.get(p._id);
        const tradeId = full?.tradeId ?? p.tradeId;
        if (!tradeId) continue;
        const tradeGroup = full?.tradeGroup ?? p.tradeGroup;
        const color = professionColor(full?.backgroundColor);
        const existing = byTrade.get(tradeId);
        if (existing) {
          existing.count += p.activeTagsIds.length;
        } else {
          byTrade.set(tradeId, {
            professionId: p._id,
            tradeId,
            tradeGroup,
            color,
            count: p.activeTagsIds.length,
          });
        }
      }
      if (byTrade.size > 0) {
        m.set(
          area.areaId.toLowerCase(),
          Array.from(byTrade.values()).sort((a, b) => b.count - a.count),
        );
      }
    }
    return m;
  }, [rawFloor, profById]);

  // Equipment / utility / manager presences per area for the current floor —
  // selectors already lower-case the areaId to match the SVG-area extraction.
  const equipmentByAreaId = useMemo(() => {
    const m = new Map<string, EquipmentPresence[]>();
    const inner = equipmentByFloorArea.get(floorId);
    if (!inner) return m;
    inner.forEach((presences, areaId) => {
      if (presences.length > 0) m.set(areaId.toLowerCase(), presences);
    });
    return m;
  }, [equipmentByFloorArea, floorId]);

  const utilitiesByAreaId = useMemo(
    () => utilitiesByFloorArea.get(floorId) ?? new Map<string, UtilityPresence[]>(),
    [utilitiesByFloorArea, floorId],
  );

  const managersByAreaId = useMemo(
    () => managersByFloorArea.get(floorId) ?? new Map<string, ManagerPresence[]>(),
    [managersByFloorArea, floorId],
  );

  // Area ids to highlight (blue fill): any area with a presence of ANY kind —
  // trades, managers, equipment, or utilities. Previously this was derived from
  // trades only, so an area holding just a manager (or just equipment) never lit
  // up even though its chip rendered.
  const highlightedAreaIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [id, presences] of personnelByAreaId) if (presences.length) ids.add(id);
    for (const [id, presences] of equipmentByAreaId) if (presences.length) ids.add(id);
    for (const [id, presences] of utilitiesByAreaId) if (presences.length) ids.add(id);
    for (const [id, presences] of managersByAreaId) if (presences.length) ids.add(id);
    return ids;
  }, [personnelByAreaId, equipmentByAreaId, utilitiesByAreaId, managersByAreaId]);

  // Floor-wide presences for the Personnel tab list (managers + equipment +
  // utilities), so it surfaces the same entities the floor-plan pins and the
  // building-view popover already show. The "Personnel" tab count stays
  // workers-only — managers and equipment aren't people.
  const floorManagers = useMemo(
    () => managersByFloor.get(floorId) ?? [],
    [managersByFloor, floorId],
  );
  const floorEquipment = useMemo(
    () => equipmentByFloor.get(floorId) ?? [],
    [equipmentByFloor, floorId],
  );
  const floorUtilities = useMemo(
    () => aggregateUtilitiesByType(utilitiesByFloor.get(floorId) ?? []),
    [utilitiesByFloor, floorId],
  );

  const close = () => setSelectedFloor(null);

  // Esc closes the modal first (if open), then the drawer. Bound to the
  // window so it works regardless of focus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isModalOpen) setIsModalOpen(false);
      else close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  // Subscribe to the same query the inner `<FloorSvg>` runs (shared via
  // queryKey through TanStack Query's cache) so this component can drive
  // its own skeleton / "no plan" / error placeholders. The actual fetch +
  // SVG inlining happen inside `<FloorSvg>` — this hook is read-only state.
  const { data: svgText, isLoading, isError } = useQuery({
    queryKey: ['storage', 'floor-view', siteId, floorId],
    queryFn: ({ signal }) => fetchFloorViewSvg(siteId!, floorId, signal),
    enabled: Boolean(siteId && floorId),
    staleTime: 5 * 60_000,
  });

  // viewBox tightening + workers-anchor measurement are owned by the
  // shared `FloorPlanView` component, so each render of the
  // SVG (drawer body, fullscreen modal) gets its own correctly-sized
  // viewBox + chip overlay.

  // Area auto-highlight (mirrors trusstor_dashboard's `paintSelectedAreas`).
  //
  // Implemented via CSS injection — a `<style>` element in the document
  // head whose contents are derived from `highlightedAreaIds`. Two reasons
  // to use stylesheet rules instead of DOM-attribute mutation:
  //   1. CSS rules survive any DOM-level mutation/reinjection of the
  //      injected SVG (which we observed wiping inline styles in dev).
  //   2. Rules with `!important` cleanly defeat any presentation
  //      attribute (`fill="#D4F9E3"`) and any `!important` CSS rule
  //      shipped inside the SVG by AI exports.
  //
  // `areaHighlightSelector` matches both bare (`area17C`) and floor-prefixed
  // (`f17area17C`) outline ids, with or without Adobe Illustrator's
  // duplicate-id suffix (`_<hash>`) — same prefix-tolerance the worker chip
  // anchors now have. See `@utils/svgMarkers`.
  useEffect(() => {
    const styleId = 'dorca-floor-area-highlight';
    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    const rules: string[] = [];
    highlightedAreaIds.forEach((areaId) => {
      rules.push(
        `${areaHighlightSelector(areaId)} {`,
        `  fill: var(--floor-selected-fill) !important;`,
        `  fill-opacity: 0.6 !important;`,
        `  opacity: 0.8 !important;`,
        `}`,
      );
    });
    styleEl.textContent = rules.join('\n');
    return () => {
      // Tied to the drawer mount, not the floorId — when the user closes
      // the drawer (component unmounts), the style element goes too.
      styleEl?.remove();
    };
  }, [highlightedAreaIds]);

  const allTabs: { id: DrawerTab; label: string; count: number; icon: React.ReactNode }[] = [
    {
      id: 'personnel',
      label: t('monitor.floorDrawer.tabs.personnel'),
      count: personnelCount,
      icon: <PersonnelIcon />,
    },
    {
      id: 'activities',
      label: t('monitor.floorDrawer.tabs.activities'),
      count: activitiesOnFloor.length,
      icon: <ActivitiesIcon />,
    },
    {
      id: 'issues',
      label: t('monitor.floorDrawer.tabs.issues'),
      count: issuesOnFloor.length,
      icon: <IssuesIcon />,
    },
    {
      id: 'safety',
      label: t('monitor.floorDrawer.tabs.safety'),
      count: safetyOnFloor.length,
      icon: <SafetyIcon />,
    },
  ];
  // Safety is a surface the role may not have: its tab is not offered at all.
  const tabs = allTabs.filter((entry) => entry.id !== 'safety' || safetyVisible);

  return (
    <>
    <aside
      className={styles.drawer}
      role="dialog"
      aria-label={t('monitor.floorDrawer.title')}
    >
      <header className={styles.header}>
        <div className={styles.titleStack}>
          <span className={styles.label}>{t('monitor.floorDrawer.label')}</span>
          <h2 className={styles.title}>{floorChip?.label ?? floorId}</h2>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={() => setIsModalOpen(true)}
            aria-label={t('monitor.floorDrawer.expand')}
          >
            <ExpandIcon />
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={close}
            aria-label={t('monitor.floorDrawer.close')}
          >
            <CloseIcon />
          </button>
        </div>
      </header>

      <ScrollArea className={styles.body} viewportClassName={styles.bodyViewport}>
        <div className={styles.svgFrame}>
          {isLoading && (
            <div
              className={styles.skeleton}
              aria-busy="true"
              aria-label={t('monitor.floorDrawer.loading')}
            >
              <div className={styles.skeletonOutline} />
            </div>
          )}
          {!isLoading && (isError || !svgText) && (
            <div className={styles.empty}>{t('monitor.floorDrawer.noPlan')}</div>
          )}
          {!isLoading && svgText && siteId && (
            <FloorPlanView
              siteId={siteId}
              floorId={floorId}
              personnelByAreaId={personnelByAreaId}
              equipmentByAreaId={equipmentByAreaId}
              utilitiesByAreaId={utilitiesByAreaId}
              managersByAreaId={managersByAreaId}
            />
          )}
        </div>

        <div className={styles.tabBar} role="tablist">
          {tabs.map((tabItem) => {
            const isActive = tab === tabItem.id;
            return (
              <button
                key={tabItem.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
                onClick={() => setTab(tabItem.id)}
              >
                <span className={styles.tabIconRow}>
                  <span className={styles.tabIcon}>{tabItem.icon}</span>
                  <span className={styles.tabCount}>{tabItem.count}</span>
                </span>
                <span className={styles.tabLabel}>{tabItem.label}</span>
              </button>
            );
          })}
        </div>

        <div className={styles.tabPanel} role="tabpanel">
          {tab === 'personnel' && (
            <PersonnelPanel
              managers={floorManagers}
              professions={personnel}
              equipment={floorEquipment}
              utilities={floorUtilities}
              emptyText={t('monitor.workforce.empty')}
            />
          )}
          {tab === 'activities' && (
            <ActivitiesPanel
              rows={activitiesOnFloor.map((item) => {
                const trade = floorItems.tradeOf(item);
                return {
                  id: item.key,
                  name: item.title,
                  trade: trade ? tradeLabel(t, trade.tradeId) : t('monitor.layers.noTrade'),
                  color: professionColor(trade?.color),
                  onOpen: () => itemDrawer.open(item),
                };
              })}
              emptyText={t('monitor.activities.empty')}
            />
          )}
          {tab === 'issues' && (
            <IssuesPanel
              rows={issuesOnFloor.map((item) => ({
                id: item.key,
                title: item.title,
                owner: item.assigneeName ?? '—',
                onOpen: () => itemDrawer.open(item),
              }))}
              loading={floorItems.issuesLoading}
              error={floorItems.issuesError}
              emptyText={t('monitor.issues.empty')}
              loadingText={t('monitor.issues.loading')}
              errorText={t('monitor.issues.failed')}
            />
          )}
          {tab === 'safety' && (
            <SafetyPanel
              rows={safetyOnFloor.map((e) => ({
                id: e._id,
                type: e.safetyViolationType,
                isCritical: e.isCritical === true,
                ts: new Date(e.timestampCreated).toLocaleTimeString(),
              }))}
              emptyText={t('monitor.safety.empty')}
            />
          )}
        </div>
      </ScrollArea>
    </aside>
    {isModalOpen && svgText && siteId && (
      <FloorPlanModal
        siteId={siteId}
        floorId={floorId}
        personnelByAreaId={personnelByAreaId}
        equipmentByAreaId={equipmentByAreaId}
        utilitiesByAreaId={utilitiesByAreaId}
        managersByAreaId={managersByAreaId}
        title={floorChip?.label ?? floorId}
        onClose={() => setIsModalOpen(false)}
      />
    )}
    {itemDrawer.drawer}
    </>
  );
}
